import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { clearWelcome, firstName, greetingFor, isWelcomePending } from '@/features/auth/welcome';
import {
  isWelcomeTourSeen,
  markWelcomeTourSeen,
  setWelcomeTourOpen,
} from '@/features/auth/welcomeTour';
import { useCartQuery } from '@/features/cart/queries';
import { ProductPreview, type ProductPreviewStep } from '@/features/catalog/ProductPreview';
import { useOrdersQuery } from '@/features/orders/queries';
import { useCurrentEstablishment, useCurrentUserName } from '@/features/organization/queries';

import { PROFILE_LABELS, profilesForNavigationItem } from './navigation';
import { RolePreview } from './RolePreview';
import { isRoleTourProfile, ROLE_TOUR_STEPS } from './roleTours';
import styles from './WelcomeBanner.module.css';

export interface WelcomeBannerProps {
  /** Opens the ⌘K palette — the one tip worth giving an internal profile on arrival. */
  readonly onSearch: () => void;
}

/** Time for the fold to finish; a fallback for a browser that never reports the transition. */
const LEAVE_MS = 320;

/**
 * A greeting on the first page after signing in, and only there: it says who is signed in and
 * the one next step that fits the profile, then gets out of the way — closed by hand, or gone as
 * soon as the user moves to another page. It never comes back in the same session.
 */
export function WelcomeBanner({ onSearch }: WelcomeBannerProps) {
  const [visible, setVisible] = useState(isWelcomePending);
  const { pathname } = useLocation();
  const landing = useRef<string | null>(null);

  useEffect(() => {
    if (visible) {
      clearWelcome();
    }
  }, [visible]);

  // The shell mounts on "/" and is redirected to the profile's home a tick later, so the page the
  // greeting belongs to is the first one that is not the bare root.
  useEffect(() => {
    if (pathname === '/') {
      return;
    }
    if (landing.current === null) {
      landing.current = pathname;
    } else if (pathname !== landing.current) {
      setVisible(false);
    }
  }, [pathname]);

  if (!visible) {
    return null;
  }

  return (
    <Greeting
      onSearch={onSearch}
      onGone={() => {
        setVisible(false);
      }}
    />
  );
}

const TOUR_STEPS = [
  {
    title: 'Adicione com +',
    detail: 'Toque no + do produto. Ele vira a pílula − 10 kg +, e o total acompanha.',
  },
  { title: 'Finalize', detail: 'Revise e confirme. O pedido sai como Enviado.' },
  {
    title: 'Acompanhe a entrega',
    detail: 'Separado, a caminho, entregue: tudo no próprio pedido.',
  },
] as const;

function Greeting({ onSearch, onGone }: WelcomeBannerProps & { readonly onGone: () => void }) {
  const { profile, user } = useAuth();
  const fullName = useCurrentUserName();
  const { establishmentId, establishmentName } = useCurrentEstablishment();
  const buys = profile !== null && profilesForNavigationItem('cart').includes(profile);
  const cart = useCartQuery(buys ? establishmentId : undefined);
  const orders = useOrdersQuery({}, buys ? establishmentId : undefined);
  const [leaving, setLeaving] = useState(false);
  const [tourSeen] = useState(() => isWelcomeTourSeen(user?.id));
  const [step, setStep] = useState<ProductPreviewStep>(1);
  const onStep = useCallback((next: ProductPreviewStep) => {
    setStep(next);
  }, []);

  useEffect(() => {
    if (!leaving) {
      return;
    }
    const timer = window.setTimeout(onGone, LEAVE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [leaving, onGone]);

  const name = firstName(fullName);
  const count = cart.data?.summary.item_count ?? 0;
  const hasOrdered =
    orders.data === undefined ? undefined : (orders.data.pages[0]?.items.length ?? 0) > 0;
  // A brand-new buying account, on its first greeting: the banner opens into the three steps.
  const tour = buys && !tourSeen && cart.data !== undefined && count === 0 && hasOrdered === false;
  // An internal profile gets its own three steps, on its first greeting too.
  const roleTour = !buys && !tourSeen && isRoleTourProfile(profile) ? profile : null;
  const touring = tour || roleTour !== null;
  const userId = user?.id;

  useEffect(() => {
    if (!touring || userId === undefined) {
      return;
    }
    markWelcomeTourSeen(userId);
    setWelcomeTourOpen(true);
    return () => {
      setWelcomeTourOpen(false);
    };
  }, [touring, userId]);

  useEffect(() => {
    if (leaving) {
      setWelcomeTourOpen(false);
    }
  }, [leaving]);

  let line: string;
  let action: ReactNode = null;
  if (buys && count > 0) {
    line = `Seu carrinho tem ${String(count)} ${count === 1 ? 'item' : 'itens'} esperando por você.`;
    action = (
      <Link to="/carrinho" viewTransition className={styles.action}>
        Continuar o pedido
      </Link>
    );
  } else if (buys && tour) {
    line =
      establishmentName === undefined
        ? 'Tudo pronto para o primeiro pedido.'
        : `Tudo pronto para o primeiro pedido em ${establishmentName}.`;
  } else if (buys) {
    line =
      establishmentName === undefined
        ? 'Tudo pronto para o pedido de hoje. Digite as quantidades direto na lista.'
        : `Tudo pronto para o pedido de hoje em ${establishmentName}.`;
  } else {
    line =
      profile === null
        ? 'Sua conta ainda não tem um perfil de acesso.'
        : roleTour !== null
          ? `Você entrou como ${PROFILE_LABELS[profile]}. Veja como o seu trabalho anda por aqui.`
          : `Você entrou como ${PROFILE_LABELS[profile]}. Dica: vá a qualquer tela pela busca.`;
    action =
      profile === null ? null : (
        <button type="button" className={styles.action} onClick={onSearch}>
          Buscar <kbd className={styles.kbd}>⌘K</kbd>
        </button>
      );
  }

  return (
    <section
      className={styles.root}
      aria-label="Boas-vindas"
      data-leaving={leaving ? '' : undefined}
      onTransitionEnd={(event) => {
        if (
          leaving &&
          event.target === event.currentTarget &&
          event.propertyName === 'grid-template-rows'
        ) {
          onGone();
        }
      }}
    >
      <div className={styles.fold}>
        <div className={styles.card}>
          <span className={styles.wave} aria-hidden="true">
            👋
          </span>
          <div className={styles.text}>
            <p className={styles.title}>
              {greetingFor()}
              {name !== undefined ? <span className={styles.name}>, {name}</span> : null}!
            </p>
            <p className={styles.line}>{line}</p>
          </div>
          {action}
          <button
            type="button"
            className={styles.close}
            aria-label="Fechar boas-vindas"
            onClick={() => {
              setLeaving(true);
            }}
          >
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
          {touring ? (
            <div className={styles.tour}>
              <div>
                <p className={styles.tourKicker}>Como funciona em 3 passos</p>
                <ol className={styles.steps}>
                  {(roleTour === null ? TOUR_STEPS : ROLE_TOUR_STEPS[roleTour]).map(
                    (item, index) => (
                      <li
                        key={item.title}
                        data-current={index + 1 === step ? '' : undefined}
                        data-done={index + 1 < step ? '' : undefined}
                      >
                        <span className={styles.stepNumber}>{index + 1}</span>
                        <span>
                          <strong>{item.title}</strong>
                          <small>{item.detail}</small>
                        </span>
                      </li>
                    ),
                  )}
                </ol>
              </div>
              {leaving ? null : roleTour === null ? (
                <ProductPreview onStep={onStep} className={styles.preview} />
              ) : (
                <RolePreview profile={roleTour} onStep={onStep} className={styles.preview} />
              )}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
